import { Controller } from "@hotwired/stimulus"
import * as tourguideModule from "@sjmc11/tourguidejs"
import { csrfToken } from "../lib/csrf.js"
import {
  CLIENT_OPTIONS, LAST_STEP, continueStep, pageForStep, resolveTarget, stepContent, stepsForPage, targetSelector,
} from "./tour/steps.js"

const { TourGuideClient } = tourguideModule.TourGuideClient ? tourguideModule : tourguideModule.default
const RESUME_PARAM = "tour"
const PICKER = '[data-tour="channel-picker"]'
const EXIT_RETRY_MS = 100
const EXIT_ATTEMPTS = 20

export default class extends Controller {
  static values = {
    url: String,
    dashboardUrl: String,
    storesUrl: String,
    page: String,
    status: String,
    lastStep: Number,
    replayStep: Number,
    media: Object,
  }

  connect() {
    this.prepareSnapshot = this.prepareSnapshot.bind(this)
    this.handleSkipClick = this.handleSkipClick.bind(this)
    document.addEventListener("turbo:before-cache", this.prepareSnapshot)
    document.addEventListener("click", this.handleSkipClick)
    this.resume()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.prepareSnapshot)
    document.removeEventListener("click", this.handleSkipClick)
    this.teardown()
  }

  // A page restored from Turbo's cache (Back) carries the tour state it was rendered
  // with, so the cached copy is marked stale and never starts the tour again.
  prepareSnapshot() {
    this.teardown()
    this.statusValue = "cached"
    this.replayStepValue = 0
  }

  // The server decides whether this restarts the first run or begins a replay.
  async replay(event) {
    event?.preventDefault()
    this.teardown()
    const response = await this.save({ restart: true })
    if (response?.redirected) return window.location.assign(response.url) // signed out
    if (response?.ok) return this.visit(this.dashboardUrlValue)

    console.error("[tour] could not start the tour", response?.status ?? "network error")
  }

  get firstRunOpen() {
    return this.statusValue === "none" || this.statusValue === "in_progress"
  }

  // An unfinished first run takes priority over any replay; it auto-starts on the
  // dashboard. A replay only runs when handed off with ?tour=1.
  resume() {
    const requested = new URLSearchParams(window.location.search).get(RESUME_PARAM) === "1"
    if (this.firstRunOpen) {
      if (requested || this.pageValue === "dashboard") this.resumeAt(this.lastStepValue, false, requested)
    } else if (this.replayStepValue > 0 && requested) {
      this.resumeAt(this.replayStepValue, true, requested)
    }
  }

  resumeAt(saved, replaying, requested) {
    const steps = stepsForPage(this.pageValue)
    const start = steps.find((step) => step.number === saved) ?? (requested ? steps.find((step) => step.number > saved) : undefined)
    if (start) return this.start(steps, start.number, replaying, saved)

    if (this.pageValue === "dashboard" && pageForStep(saved) === "stores") {
      this.start([ continueStep(saved) ], saved, replaying, saved)
    }
  }

  start(steps, number, replaying, saved) {
    const width = window.innerWidth
    const handsOff = steps.at(-1).resume || steps.at(-1).number < LAST_STEP
    this.pageSteps = steps
    this.replaying = replaying
    this.ending = false
    const client = this.createClient({
      ...CLIENT_OPTIONS,
      steps: steps.map((step) => this.buildStep(step, width)),
      finishLabel: handsOff ? "Next" : "Finish",
    })
    this.client = client
    // The library only marks a step change as busy (_promiseWaiting, tg-dialog-loading)
    // when a before-change callback exists. Without it, a click mid-change re-enters the
    // step, and an exit is undone when the change finishes positioning the dialog.
    client.onBeforeStepChange(() => {})
    client.onAfterStepChange((_previous, index) => {
      if (client === this.client && !this.ending) this.save(this.progress(this.pageSteps[index].number))
    })
    client.onFinish(() => this.finish(client))
    client.onBeforeExit(() => this.skip(client))
    client.activeStep = steps.findIndex((step) => step.number === number)
    // start() keeps working asynchronously; if the page changed meanwhile, clean up
    // whatever it attached to the new page.
    client.start().catch(() => {}).then(() => {
      if (this.client !== client) this.dispose(client)
    })
    if (number !== saved) this.save(this.progress(number))
  }

  createClient(options) {
    return new TourGuideClient(options)
  }

  buildStep(step, width) {
    const built = {
      title: step.title,
      content: stepContent(step, width, this.mediaValue),
      target: resolveTarget(targetSelector(step, width)),
    }
    // Runs before the library sizes and positions the dialog for this step.
    built.beforeEnter = () => {
      this.client?.dialog.classList.toggle("tg-dialog--media", Boolean(step.media))
      if (step.picker) this.openPicker()
    }
    if (step.picker) built.afterLeave = () => this.closePicker()
    return built
  }

  async finish(client) {
    if (client !== this.client) return

    this.ending = true
    const last = this.pageSteps.at(-1)
    if (last.resume) return this.visit(this.storesUrlValue) // the saved step is already stored
    if (last.number < LAST_STEP) {
      await this.save(this.progress(last.number + 1))
      return this.visit(this.storesUrlValue)
    }

    this.save(this.replaying ? { replay: true, status: "completed" } : { status: "completed", step: LAST_STEP })
  }

  // Runs whenever the library exits. A finished or torn-down tour is not a skip.
  skip(client) {
    if (client !== this.client) return

    this.closePicker()
    if (this.ending) return

    this.ending = true
    const current = this.pageSteps[client.activeStep].number
    this.save(this.replaying ? { replay: true, status: "skipped" } : { status: "skipped", step: current })
  }

  handleSkipClick(event) {
    if (this.client && event.target.closest?.("[data-tour-skip]")) this.exitWhenIdle(this.client)
  }

  // exit() refuses while a step is still entering (e.g. opening the picker), so wait
  // for the step instead of dropping the seller's skip.
  async exitWhenIdle(client) {
    for (let attempt = 0; attempt < EXIT_ATTEMPTS && client === this.client; attempt++) {
      try {
        return await client.exit()
      } catch {
        await new Promise((resolve) => setTimeout(resolve, EXIT_RETRY_MS))
      }
    }
  }

  // Leaving a page (link, Back, Turbo snapshot) removes the tour without counting a
  // skip; the next page resumes from the saved step.
  teardown() {
    const client = this.client
    if (!client) return

    this.client = null
    this.closePicker()
    this.dispose(client)
  }

  // Closes through the library's own exit path, then removes its nodes right away so
  // nothing reaches Turbo's snapshot or the next page.
  dispose(client) {
    client.exit().catch(() => client.destroyListeners())
    const removeNodes = () => {
      client.dialog.remove()
      client.backdrop.remove()
    }
    removeNodes()
    setTimeout(removeNodes, 0) // the library appends its dialog after an await
    if (!anotherTourShown(client)) document.body.classList.remove("tg-no-interaction")
  }

  // The picker normally opens with showModal(), which would sit above the tour and
  // block its buttons; show() keeps the tour clickable while the page stays inert.
  openPicker() {
    const picker = document.querySelector(PICKER)
    if (picker && !picker.open) picker.show()
  }

  closePicker() {
    const picker = document.querySelector(PICKER)
    if (picker?.open) picker.close()
  }

  progress(number) {
    return this.replaying ? { replay: true, step: number } : { step: number }
  }

  // Saves go out one at a time so a slow earlier step can never land after a later one.
  // Resolves to the response, or undefined when offline; the tour keeps running either way.
  save(payload) {
    const body = new FormData()
    Object.entries(payload).forEach(([key, value]) => body.append(key, value))
    const send = () => fetch(this.urlValue, {
      method: "PATCH",
      body,
      keepalive: true,
      credentials: "same-origin",
      headers: { "X-CSRF-Token": csrfToken() },
    }).catch(() => undefined)
    this.saving = (this.saving ?? Promise.resolve()).then(send)
    return this.saving
  }

  visit(url) {
    const target = `${url}?${RESUME_PARAM}=1`
    if (window.Turbo) window.Turbo.visit(target)
    else window.location.assign(target)
  }
}

function anotherTourShown(client) {
  return [ ...document.querySelectorAll(".tg-dialog") ].some((dialog) => dialog !== client.dialog && dialog.style.display !== "none")
}
